<template lang="html">
	<div class="campaign-type-filter" @keydown.esc="clearFilters">
		<templates-search />
		<div class="campaign-type-filter-options">
			<templates-filter-option
				v-for="section in optionSections"
				:key="section.slug"
				:title="section.title"
				:slug="section.slug"
				:options="section.options"
				:show-options="section.showOptions"
				@scrollTop="scrollTop"
			/>
		</div>
		<div class="campaign-type-devices">
			<button
				class="campaign-type-device all-devices"
				:class="{ selected: isDeviceSelected('desktop') }"
				@click="setDevice('desktop')"
			>
				<span class="campaign-type-device-img">
					<img
						class="device-icons-desktop"
						src="../../assets/images/icons/device-all.svg"
						alt="Desktop/Tablet Icon"
					/>
				</span>
				<span class="campaign-type-device-name">Desktop/Tablet <small>(Responsive)</small></span>
			</button>
			<button
				class="campaign-type-device mobile"
				:class="{ selected: isDeviceSelected('mobile') }"
				@click="setDevice('mobile')"
			>
				<span class="campaign-type-device-img">
					<img
						class="device-icons-mobile"
						src="../../assets/images/icons/device-mobile.svg"
						alt="Mobile Icon"
					/>
				</span>
				<span class="campaign-type-device-name">Mobile Optimized</span>
			</button>
		</div>
		<core-button class="clear-button" color="blue" @click="clearFilters">Clear Filters</core-button>
	</div>
</template>

<script lang="js">
import { createNamespacedHelpers } from 'vuex';
const { mapState, mapGetters } = createNamespacedHelpers('templates');
import { scrolltoref } from '@/components/mixins/scrolltoref';

export default {
	mixins: [scrolltoref],
	data() {
		return {
			scrolling: false,
		};
	},
	computed: {
		...mapState(['sort', 'search', 'activeType', 'loading', 'filterOptions']),
		...mapGetters(['filters']),
		/**
		 * The options for the filter sections.
		 *
		 * @returns {Array} The filter sections.
		 */
		optionSections() {
			return [
				{
					title: 'Seasonal',
					slug: 'seasons',
					options: this.options.seasons,
					showOptions: true,
				},
				{
					title: 'Goals',
					slug: 'goals',
					options: this.options.goals,
					showOptions: true,
				},
				{
					title: 'Industry',
					slug: 'categories',
					options: this.options.categories,
					showOptions: true,
				},
				{
					title: 'Features',
					slug: 'features',
					options: this.options.features,
					showOptions: true,
				},
			];
		},
		/**
		 * Combine the options for each section into a single object.
		 *
		 * @returns {object} The combined options.
		 */
		options() {
			let options = {};

			this.filterOptions.forEach((o) => {
				options[o.slug] = o.values;
			});

			return options;
		},
	},
	mounted() {
		// There are instances where smaller screens will scroll to the middle
		// of the page when navigating to the templates page. This mostly happens
		// when clicking on a "create new" button from the Dashboard. This ensures
		// that the screen is always at the top.
		this.scrollToRef('.omapi-plugin-header');
	},
	beforeDestroy() {
		// Clear filters when the user navigates away from the templates page.
		this.clearFilters();
	},
	methods: {
		/**
		 * Clear all filters, including device.
		 *
		 * @returns {void}
		 */
		clearFilters() {
			this.$store.commit('templates/setSearch', '');
			this.$store.commit('templates/clearFilters');
			this.setDevice('desktop');
			this.scrollTop();
		},
		/**
		 * Determine if the given device type is selected/active.
		 *
		 * @param {string} type The device type.
		 *
		 * @returns {boolean} True if the device is selected.
		 */
		isDeviceSelected(type) {
			return type === this.filters.device;
		},
		/**
		 * Set the give type as the device filter.
		 *
		 * @param {string} type The device type.
		 *
		 * @returns {void}
		 */
		setDevice(type) {
			this.$store.commit('templates/setFilter', { filter: 'device', value: type || '' });
			this.scrollTop();
		},
		/**
		 * Scroll to the top of the templates list.
		 *
		 * @returns {void}
		 */
		scrollTop() {
			if (this.scrolling) {
				return;
			}

			this.scrolling = true;

			setTimeout(() => {
				this.scrollToRef('.template-content-right', {
					offset: -100,
				});

				this.scrolling = false;
			}, 500);
		},
	},
};
</script>
<style lang="scss">
@import '../../assets/scss/variables';

.campaign-type-filter-options {
	margin-top: 24px;
}

.campaign-type-devices {
	.campaign-type-device {
		background: #ffffff;
		border: 1px solid $color_grey-border;
		padding: 10px 20px;
		width: 100%;
		border-radius: $button_border-radius;
		margin-bottom: 8px;
		font-size: 13px;
		text-align: left;
		color: $font_dark-color;
		display: flex;
		align-items: center;

		&.selected,
		&:hover {
			color: $color_black;
			border-color: $color_blue-primary;
		}

		&:hover {
			cursor: pointer;
		}

		.campaign-type-device-img {
			width: 33px;
			margin-right: 10px;
		}

		.campaign-type-device-name {
			small {
				font-size: 11px;
			}
		}

		&.selected {
			.campaign-type-device-name {
				position: relative;
				overflow: visible;

				&::before {
					content: '';
					background: url('../../assets/images/icons/green-success-circle.svg');
					background-size: contain;
					height: 13px;
					width: 13px;
					position: absolute;
					right: -18px;
					top: 2px;
				}
			}
		}
	}
}

.clear-button {
	font-weight: 400 !important;
	width: 100%;
}
</style>
